import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  CheckCircle2,
  MessageSquare,
  Upload as UploadIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationRow,
  type NotificationType,
} from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

type Props = {
  /** Watch base — "/app/watch" or "/admin/watch". Used to rewrite server links `/videos/{id}`. */
  watchPath: string;
  /** Layout prefix — "/app" or "/admin". Used to rewrite server links like `/app/trash` when this page renders inside /admin. */
  basePrefix: "/app" | "/admin";
};

type Filter = "all" | "unread";

/**
 * Notifications feed — GET /api/notifications with newest-first ordering.
 * Server-side onlyUnread filter (?onlyUnread=true) is used for the Unread tab
 * so we don't ship a big list to the client just to hide most of it.
 *
 * Link rewrite:
 *  - `/videos/{rootId}` (with optional ?commentId=X) → `{watchPath}/{rootId}?...`
 *  - `/app/…` (currently just `/app/trash`) → `${basePrefix}/…` so an admin
 *    clicking an auto-delete-warning stays inside /admin.
 *
 * Click flow: navigate → mark-read (fire and forget). Reading is idempotent.
 */
export function NotificationsPage({ watchPath, basePrefix }: Props) {
  const [filter, setFilter] = useState<Filter>("all");
  const { data: items, isLoading, isError, refetch } = useNotifications({
    onlyUnread: filter === "unread",
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();
  const navigate = useNavigate();

  const unreadCount = useMemo(
    () => (items || []).filter((n) => !n.readAt).length,
    [items]
  );

  function resolveLink(link: string | null): string | null {
    if (!link) return null;
    if (link.startsWith("/videos/")) {
      return watchPath + link.slice("/videos".length);
    }
    if (link.startsWith("/app/") && basePrefix !== "/app") {
      return basePrefix + link.slice("/app".length);
    }
    return link;
  }

  function handleOpen(n: NotificationRow) {
    const to = resolveLink(n.link);
    if (!n.readAt) {
      // fire and forget — server is idempotent, cache invalidates on success
      markRead.mutate(n.id);
    }
    if (to) navigate(to);
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">Notifications</h1>
          {unreadCount > 0 && (
            <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand">
              {unreadCount} unread
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Uploads, mentions, approval changes, and retention warnings. Read notifications older than 45 days are auto-cleared.
        </p>
      </header>

      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
          <FilterTab active={filter === "all"} onClick={() => setFilter("all")}>
            All
          </FilterTab>
          <FilterTab active={filter === "unread"} onClick={() => setFilter("unread")}>
            Unread
          </FilterTab>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => markAllRead.mutate()}
          disabled={markAllRead.isPending || unreadCount === 0}
          aria-label="Mark all read"
        >
          <CheckCheck />
          <span className="hidden sm:inline">
            {markAllRead.isPending ? "Marking…" : "Mark all read"}
          </span>
        </Button>
      </div>

      {isLoading && (
        <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          Loading notifications…
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-4 text-sm text-destructive">
          Couldn't load notifications.{" "}
          <button type="button" onClick={() => refetch()} className="underline">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (items?.length ?? 0) === 0 && (
        <div className="grid place-items-center rounded-xl border border-dashed border-border bg-card px-4 py-16 text-center">
          <Bell className="mb-3 h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
          <div className="font-medium text-foreground">
            {filter === "unread" ? "No unread notifications" : "You're all caught up"}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {filter === "unread"
              ? "Nothing waiting on you right now."
              : "Uploads, mentions, and approval updates will show up here."}
          </p>
        </div>
      )}

      {!isLoading && !isError && (items?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-2">
          {items!.map((n) => (
            <NotificationRowUi
              key={n.id}
              item={n}
              onOpen={() => handleOpen(n)}
              onDismiss={() => dismiss.mutate(n.id)}
              onMarkRead={() => markRead.mutate(n.id)}
              hasLink={!!n.link}
              busyDismiss={dismiss.isPending && dismiss.variables === n.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md px-3 py-1 text-sm transition-colors",
        active
          ? "bg-secondary text-foreground"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function TypeIcon({ type, className }: { type: NotificationType; className?: string }) {
  const c = cn("h-4 w-4", className);
  switch (type) {
    case "auto_delete_warning":
      return <AlertTriangle className={c} strokeWidth={1.75} />;
    case "comment_mention":
      return <MessageSquare className={c} strokeWidth={1.75} />;
    case "approval_change":
      return <CheckCircle2 className={c} strokeWidth={1.75} />;
    case "video_uploaded":
      return <UploadIcon className={c} strokeWidth={1.75} />;
    default:
      return <Bell className={c} strokeWidth={1.75} />;
  }
}

function typeIconWrapClass(type: NotificationType): string {
  switch (type) {
    case "auto_delete_warning":
      return "bg-amber-500/10 text-amber-500 dark:text-amber-400";
    case "comment_mention":
      return "bg-blue-500/10 text-blue-500 dark:text-blue-400";
    case "approval_change":
      return "bg-emerald-500/10 text-emerald-500 dark:text-emerald-400";
    case "video_uploaded":
      return "bg-brand/10 text-brand";
    default:
      return "bg-secondary text-muted-foreground";
  }
}

function NotificationRowUi({
  item,
  onOpen,
  onDismiss,
  onMarkRead,
  hasLink,
  busyDismiss,
}: {
  item: NotificationRow;
  onOpen: () => void;
  onDismiss: () => void;
  onMarkRead: () => void;
  hasLink: boolean;
  busyDismiss: boolean;
}) {
  const unread = !item.readAt;

  return (
    <li
      className={cn(
        "group flex items-start gap-3 rounded-lg border bg-card px-3 py-3 transition-colors",
        unread ? "border-border" : "border-border/60 opacity-80",
        hasLink && "cursor-pointer hover:bg-secondary/40"
      )}
      onClick={hasLink ? onOpen : undefined}
      role={hasLink ? "button" : undefined}
      tabIndex={hasLink ? 0 : undefined}
      onKeyDown={
        hasLink
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <div
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-md",
          typeIconWrapClass(item.type)
        )}
      >
        <TypeIcon type={item.type} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "truncate text-sm",
              unread ? "font-medium text-foreground" : "text-foreground/80"
            )}
          >
            {item.title}
          </span>
          {unread && (
            <span
              aria-label="Unread"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand"
            />
          )}
        </div>
        {item.body && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.body}</div>
        )}
        <div className="mt-1 text-xs text-muted-foreground">{relativeTime(item.createdAt)}</div>
      </div>

      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        {unread && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead();
            }}
            aria-label="Mark as read"
            title="Mark as read"
          >
            <CheckCircle2 />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          disabled={busyDismiss}
          aria-label="Dismiss"
          title="Dismiss"
          className="text-muted-foreground hover:text-destructive"
        >
          <X />
        </Button>
      </div>
    </li>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
